/* =============================================================
   VisaTypePage — /visa/:country
   Two top-level options: Өөрөө мэдүүлэх | Биднээр мэдүүлэх
   Self-apply: expands to show visa sub-types + bundle upsell
   ============================================================= */
import { useParams, useLocation } from "wouter";
import { motion } from "framer-motion";
import {
  ArrowLeft, BookOpen, Star, ArrowRight,
  CheckCircle2, Shield,
} from "lucide-react";
import Header from "@/components/Header";

/* ── Country meta ─────────────────────────────────────────────── */
const COUNTRY_META: Record<string, { flag: string; name: string }> = {
  usa:        { flag: "🇺🇸", name: "АНУ" },
  australia:  { flag: "🇦🇺", name: "Австрали" },
  canada:     { flag: "🇨🇦", name: "Канад" },
  ireland:    { flag: "🇮🇪", name: "Ирланд" },
  czech:      { flag: "🇨🇿", name: "Чех Улс" },
  schengen:   { flag: "🇪🇺", name: "Шенген" },
  uk:         { flag: "🇬🇧", name: "Их Британи" },
  newzealand: { flag: "🇳🇿", name: "Шинэ Зеланд" },
};

/* ── Professional features per country ───────────────────────── */
const PRO_FEATURES: Record<string, string[]> = {
  usa: [
    "Бүх материалыг манай агент бэлдэнэ",
    "DS-160 зөв бөглөж өгнө",
    "Ярилцлагад бэлдэж өгнө",
    "Ногоон карт, ажлын визний боломж",
    "Хуулийн зөвлөхтэй холбоно",
  ],
  default: [
    "Бүх материалыг манай агент бэлдэнэ",
    "Визийн мэдүүлгийг таны өмнөөс хийнэ",
    "Татгалзлын эрсдлийг бууруулна",
    "Мэргэжлийн зөвлөх дэмжлэг",
    "Хурдан, найдвартай үйлчилгээ",
  ],
};

/* ── Professional route per country ──────────────────────────── */
const PRO_ROUTE: Record<string, string> = {
  usa:        "/visa/usa/travel/full-service",
  australia:  "/visa/australia/travel/full-service",
  canada:     "/visa/canada/travel/full-service",
  ireland:    "/visa/ireland/travel/full-service",
  czech:      "/visa/czech/work/work-full-service",
  schengen:   "/visa/schengen/travel/full-service",
  uk:         "/visa/uk/travel/full-service",
  newzealand: "/visa/newzealand/travel/full-service",
};

/* ── Main page ────────────────────────────────────────────────── */
export default function VisaTypePage() {
  const params = useParams<{ country: string }>();
  const [, navigate] = useLocation();
  const country     = COUNTRY_META[params.country] ?? { flag: "🌍", name: params.country };
  const proFeatures = PRO_FEATURES[params.country] ?? PRO_FEATURES.default;
  const proRoute    = PRO_ROUTE[params.country] ?? `/visa/${params.country}/travel/full-service`;
  const selfRoute   = `/visa/${params.country}/self`;
  // Czech has no self-apply option — only professional service
  const showSelfApply = params.country !== "czech";

  return (
    <div
      className="min-h-screen"
      style={{ background: "linear-gradient(135deg, #0D0A2E 0%, #130D3A 45%, #0F0B35 100%)" }}
    >
      <Header />

      <div className="container pt-24 pb-16 max-w-2xl mx-auto">
        {/* Back button */}
        <motion.button
          initial={{ opacity: 0, x: -12 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate("/")}
          className="flex items-center gap-2 mb-8 text-white/50 hover:text-white/80 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Буцах</span>
        </motion.button>

        {/* Page header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 text-center"
        >
          <span className="text-5xl mb-4 block">{country.flag}</span>
          <h1
            className="text-white font-black text-3xl leading-tight mb-2"
            style={{ fontFamily: "'Sora', sans-serif", letterSpacing: "-0.02em" }}
          >
            {country.name} виз мэдүүлэх
          </h1>
          <p className="text-white/50 text-sm">
            Та хэрхэн мэдүүлэхээ сонгоно уу
          </p>
        </motion.div>

        {/* Two option cards */}
        <div className="flex flex-col gap-5">

          {/* Option 1: Self-apply — navigates to dedicated page */}
          {showSelfApply && <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(selfRoute)}
            className="w-full rounded-2xl overflow-hidden text-left transition-all duration-200"
            style={{
              background: "rgba(255,255,255,0.04)",
              border: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(124,58,237,0.15)", border: "1px solid rgba(124,58,237,0.25)" }}
                  >
                    <BookOpen className="w-6 h-6 text-violet-400" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-xl leading-tight">
                      Өөрөө мэдүүлэх
                    </h2>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
                      style={{ background: "rgba(124,58,237,0.15)", color: "rgba(167,139,250,0.9)" }}
                    >
                      💰 Мөнгөнд хэмнэлттэй
                    </span>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-white/30 flex-shrink-0 mt-1" />
              </div>
              <p className="text-white/50 text-sm leading-relaxed">
                Визийн төрлөө сонгоод алхам алхмаар зааварчилгааг ашиглаарай.
              </p>
            </div>
          </motion.button>}

          {/* Option 2: Professional service */}
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            whileHover={{ scale: 1.02, y: -2 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => navigate(proRoute)}
            className="w-full rounded-2xl overflow-hidden text-left transition-all duration-200"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #4F46E5 100%)",
              border: "1px solid rgba(124,58,237,0.5)",
              boxShadow: "0 8px 32px rgba(124,58,237,0.3)",
            }}
          >
            <div className="p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(255,255,255,0.18)" }}
                  >
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-white font-bold text-xl leading-tight">
                      Биднээр мэдүүлэх
                    </h2>
                    <span
                      className="text-xs px-2 py-0.5 rounded-full mt-1 inline-block"
                      style={{ background: "rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.9)" }}
                    >
                      ⭐ Мэргэжлийн үйлчилгээ
                    </span>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-white/60 flex-shrink-0 mt-1" />
              </div>
              <p className="text-white/80 text-sm mb-4 leading-relaxed">
                Хэрэв та өөрөө мэдүүлэхэд хүндрэлтэй байвал манай мэргэжлийн агент таньд үйлчилгээ шууд үзүүлнэ.
              </p>
              <div className="flex flex-col gap-2">
                {proFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-white/80 flex-shrink-0" />
                    <span className="text-white/90 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.button>

        </div>

        {/* Bottom note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="mt-8 text-center"
        >
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm"
            style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
          >
            <Shield className="w-4 h-4 text-violet-400" />
            <span className="text-white/50">Зөвлөгөө авбал үйлчилгээний төлбөр хасагдана</span>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
